import { AdminApiError, type AdminClient, type WalletLedgerEntry } from '@apx/admin-sdk';
import { wireDatePickers } from '../date-picker.js';
import { askConfirm, html, infoModal } from '../modal.js';
import { fmtDateTime, fmtMoney } from '../format.js';
import { toDecimalString, toMajorNumber } from '@apx/money';
import { applyHiddenCols, mountColumnPicker } from '../column-picker.js';
import { exportXlsx } from '../xlsx.js';
import { applySort, getSortState, sortableTh, wireSortHeaders } from '../sort.js';
import { mountSavedViews } from '../saved-views.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';

export interface WalletLedgerPageDeps {
  client: AdminClient;
  integratorId: string;
}

export async function renderWalletLedgerPage(
  root: HTMLElement,
  deps: WalletLedgerPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Integrator call log</h1>
    <p class="page-sub">
      Audit copy of every outbound wallet call apx made to the
      integrator (HMAC-signed). Idempotent via <code>requestId</code>. NOT
      the source of truth — integrator's ledger is.
    </p>

    <div class="card">
      <h2>Filters</h2>
      <div class="row" style="flex-wrap:wrap;gap:12px">
        <label style="min-width:260px;flex:1">Player ID (uuid)
          <input id="wl-player" type="text" placeholder="optional uuid" />
        </label>
        <label>Type
          <select id="wl-type">
            <option value="">all</option>
            <option value="DEBIT_BET">DEBIT_BET</option>
            <option value="CREDIT_WIN">CREDIT_WIN</option>
            <option value="CREDIT_REFUND">CREDIT_REFUND</option>
            <option value="ROLLBACK">ROLLBACK</option>
            <option value="RECONCILE_MANUAL">RECONCILE_MANUAL</option>
          </select>
        </label>
        <label>Status
          <select id="wl-status">
            <option value="">all</option>
            <option value="pending">pending</option>
            <option value="confirmed">confirmed</option>
            <option value="failed">failed</option>
            <option value="rolled_back">rolled_back</option>
            <option value="reconcile_manual">reconcile_manual</option>
          </select>
        </label>
        <label>Currency
          <input id="wl-currency" type="text" placeholder="USD" />
        </label>
        <label>Window
          <select id="wl-since">
            <option value="">All time</option>
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range…</option>
          </select>
        </label>
        <span id="wl-custom-range" class="r-custom-range hidden">
          <span class="filter-field">
            <label for="wl-from">From</label>
            <input id="wl-from" type="datetime-local" />
          </span>
          <span class="filter-field">
            <label for="wl-to">To</label>
            <input id="wl-to" type="datetime-local" />
          </span>
        </span>
        <label>Limit
          <input id="wl-limit" type="number" min="10" max="200" value="50" />
        </label>
        <button id="wl-run" class="primary">Apply</button>
        <button id="wl-export" class="ghost">Export Excel</button>
        <div id="wl-cols"></div>
        <div id="wl-views" class="row" style="margin:0 0 0 auto;gap:6px"></div>
      </div>
    </div>

    <div class="card">
      <h2>Entries</h2>
      <div id="wl-list">${skeletonTable({ rows: 10, columns: 8 })}</div>
    </div>
  `;

  wireDatePickers(root);

  const listEl = root.querySelector<HTMLElement>('#wl-list')!;

  let accumulated: WalletLedgerEntry[] = [];
  let lastCursor: string | undefined;
  let lastFilters: Record<string, string | undefined> = {};
  let lastLimit = 50;

  function readFilters(): Record<string, string | undefined> {
    const player = root.querySelector<HTMLInputElement>('#wl-player')!.value.trim();
    const type = root.querySelector<HTMLSelectElement>('#wl-type')!.value;
    const status = root.querySelector<HTMLSelectElement>('#wl-status')?.value ?? '';
    const currency = root.querySelector<HTMLInputElement>('#wl-currency')!.value.trim();
    const since = root.querySelector<HTMLSelectElement>('#wl-since')!.value;
    const fromRaw = root.querySelector<HTMLInputElement>('#wl-from')!.value;
    const toRaw = root.querySelector<HTMLInputElement>('#wl-to')!.value;
    let from: string | undefined;
    let to: string | undefined;
    if (since === 'custom') {
      from = fromRaw ? new Date(fromRaw).toISOString() : undefined;
      to = toRaw ? new Date(toRaw).toISOString() : undefined;
    } else if (since) {
      const days: Record<string, number> = { '24h': 1, '7d': 7, '30d': 30 };
      from = new Date(Date.now() - (days[since] ?? 0) * 86_400_000).toISOString();
    }
    return {
      playerId: player || undefined,
      type: type || undefined,
      status: status || undefined,
      fiatCurrency: currency || undefined,
      from,
      to,
    };
  }

  function rerenderInPlace(): void {
    listEl.innerHTML = `<div style="max-height:62vh;overflow:auto">${renderTable(accumulated)}</div>
      <div class="row" style="margin-top:10px">
        ${lastCursor ? '<button id="wl-load-more">Load more</button>' : '<span class="muted">End of ledger.</span>'}
        <span class="muted" style="margin-left:auto">${accumulated.length} loaded</span>
      </div>`;
    listEl
      .querySelector<HTMLButtonElement>('#wl-load-more')
      ?.addEventListener('click', () => void refresh(true));
    const table = listEl.querySelector<HTMLElement>('table');
    if (table) wireSortHeaders(table, 'wallet-ledger', () => rerenderInPlace());
    applyHiddenCols(table, 'wallet-ledger');
    bindReplay();
    bindDrill();
  }

  async function refresh(append = false): Promise<void> {
    if (!append) {
      accumulated = [];
      lastCursor = undefined;
      lastFilters = readFilters();
      lastLimit = Number(root.querySelector<HTMLInputElement>('#wl-limit')!.value) || 50;
      listEl.innerHTML = skeletonTable({ rows: 10, columns: 8 });
    }
    try {
      const res = await deps.client.listWalletTransactions(deps.integratorId, {
        ...(lastFilters as Parameters<typeof deps.client.listWalletTransactions>[1]),
        limit: lastLimit,
        cursor: append ? lastCursor : undefined,
      });
      accumulated = accumulated.concat(res.entries);
      lastCursor = res.nextCursor;
      rerenderInPlace();
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(entries: WalletLedgerEntry[]): string {
    if (entries.length === 0) return '<p class="muted">No matching entries.</p>';
    const sort = getSortState('wallet-ledger');
    const sorted = applySort(entries, sort, (e, key) => {
      switch (key) {
        case 'createdAt':
          return e.createdAt;
        case 'type':
          return e.type;
        case 'creditAmount':
          return toMajorNumber(e.creditAmount, cur(e));
        case 'balanceAfter':
          return e.balanceAfter == null ? null : toMajorNumber(e.balanceAfter, cur(e));
        case 'fiatCurrency':
          return e.fiatCurrency;
        default:
          return '';
      }
    });
    return `<table>
      <thead><tr>
        ${sortableTh('When', 'createdAt', sort)}
        ${sortableTh('Type', 'type', sort)}
        <th>Status</th>
        ${sortableTh('Amount', 'creditAmount', sort)}
        ${sortableTh('Balance after', 'balanceAfter', sort)}
        <th>Fiat</th>
        ${sortableTh('Currency', 'fiatCurrency', sort)}
        <th>Player</th>
        <th>Latency</th>
        <th>Integrator tx</th>
        <th>Reason</th>
        <th>Request id</th>
        <th></th>
      </tr></thead>
      <tbody>${sorted.map(rowHtml).join('')}</tbody>
    </table>`;
  }

  function statusPill(status: string | undefined): string {
    if (!status) return '';
    const cls =
      status === 'confirmed'
        ? 'live'
        : status === 'pending'
        ? ''
        : status === 'failed'
        ? 'warn'
        : status === 'rolled_back'
        ? 'warn'
        : status === 'reconcile_manual'
        ? 'warn'
        : '';
    return `<span class="pill ${cls}">${status}</span>`;
  }

  function rowHtml(e: WalletLedgerEntry): string {
    const amt = Number(e.creditAmount);
    const isCredit =
      e.type === 'CREDIT_WIN' ||
      e.type === 'CREDIT_REFUND' ||
      e.type === 'CREDIT' ||
      e.type === 'ROLLBACK' ||
      (e.type === 'ADJUST' && amt > 0);
    const color = isCredit ? 'var(--success)' : 'var(--error)';
    const sign = isCredit ? '+' : '-';
    const playerLabel = e.playerUsername
      ? escapeHtml(e.playerUsername)
      : '<span class="muted">(anon)</span>';
    const balanceCell =
      e.balanceAfter === null
        ? '<span class="muted">—</span>'
        : fmtMoney(e.balanceAfter, cur(e));
    // The wallet amount is the bet amount 1:1, in `fiatCurrency`.
    const fiatCell =
      e.fiatAmount === null || e.fiatAmount === undefined
        ? '<span class="muted">—</span>'
        : `${sign}${fmtMoney(e.fiatAmount, e.fiatCurrency || cur(e))}`;
    const integratorCell = e.integratorTxId
      ? `<code style="font-size:11px">${escapeHtml(e.integratorTxId).slice(0, 16)}…</code>`
      : '<span class="muted">—</span>';
    const latencyCell =
      e.latencyMs != null
        ? `<span class="muted" style="font-size:12px">${e.latencyMs}ms${e.attempt && e.attempt > 0 ? ` ·${e.attempt}` : ''}</span>`
        : e.lastError
        ? `<span style="color:var(--error);font-size:11px" title="${escapeHtml(e.lastError)}">${escapeHtml(e.lastError).slice(0, 40)}…</span>`
        : '<span class="muted">—</span>';
    const canReplay =
      e.status === 'failed' || e.status === 'pending';
    const actionCell = canReplay
      ? `<button class="wl-replay" data-id="${e.id}">Replay</button>`
      : '';
    return `<tr data-row-id="${e.id}" class="wl-row" style="cursor:pointer">
      <td>${fmtDateTime(e.createdAt)}</td>
      <td><span class="pill">${escapeHtml(e.type)}</span></td>
      <td>${statusPill(e.status)}</td>
      <td style="color:${color};font-weight:600;font-variant-numeric:tabular-nums">${sign}${fmtMoney(e.creditAmount, cur(e))}</td>
      <td style="font-variant-numeric:tabular-nums">${balanceCell}</td>
      <td style="font-variant-numeric:tabular-nums">${fiatCell}</td>
      <td><code>${escapeHtml(e.fiatCurrency || cur(e))}</code></td>
      <td><a href="/integrators/${encodeURIComponent(deps.integratorId)}/players/${e.playerId}" style="color:var(--accent-2);text-decoration:none">${playerLabel}<br><span class="muted" style="font-size:11px"><code>${e.playerId.slice(0, 8)}…</code></span></a></td>
      <td>${latencyCell}</td>
      <td>${integratorCell}</td>
      <td><span class="muted" style="font-size:12px">${escapeHtml(e.reason)}</span></td>
      <td><code style="font-size:11px">${escapeHtml(e.requestId)}</code></td>
      <td style="text-align:right">${actionCell}</td>
    </tr>`;
  }

  function openLedgerDetail(e: WalletLedgerEntry): void {
    const kv = (k: string, v: string | null | undefined): ReturnType<typeof html> =>
      html`<div><div class="detail-k">${k}</div><div class="detail-v">${v == null || v === '' ? '—' : v}</div></div>`;
    const amt = Number(e.creditAmount);
    const isCredit =
      e.type === 'CREDIT_WIN' ||
      e.type === 'CREDIT_REFUND' ||
      e.type === 'CREDIT' ||
      e.type === 'ROLLBACK' ||
      (e.type === 'ADJUST' && amt > 0);
    const sign = isCredit ? '+' : '-';
    const playerHref = `/integrators/${encodeURIComponent(deps.integratorId)}/players/${e.playerId}`;
    const errBlock = e.lastError
      ? html`<div class="detail-section-h" style="color:var(--error)">Last error</div>
          <pre class="detail-json" style="color:var(--error);border-color:rgba(248,113,113,0.35)">${e.lastError}</pre>`
      : html``;
    const close = infoModal({
      title: `${e.type} · ${sign}${fmtMoney(e.creditAmount, cur(e))} ${cur(e)}`,
      subtitle: `${e.status ?? 'unknown'} · ${fmtDateTime(e.createdAt)}`,
      wide: true,
      body: html`
        <div class="detail-section-h">Amounts</div>
        <div class="detail-grid">
          ${kv('Amount', `${sign}${fmtMoney(e.creditAmount, cur(e))} ${cur(e)}`)}
          ${kv('Balance after', e.balanceAfter == null ? '—' : `${fmtMoney(e.balanceAfter, cur(e))} ${cur(e)}`)}
          ${kv('Fiat amount', e.fiatAmount == null ? null : `${sign}${fmtMoney(e.fiatAmount, e.fiatCurrency || cur(e))}`)}
          ${kv('Fiat currency', e.fiatCurrency)}
          ${kv('Rate (ppm)', e.ratePpm == null ? null : String(e.ratePpm))}
        </div>
        <div class="detail-section-h">Delivery</div>
        <div class="detail-grid">
          ${kv('Status', e.status)}
          ${kv('Attempt', String(e.attempt ?? 0))}
          ${kv('Latency', e.latencyMs == null ? null : `${e.latencyMs}ms`)}
          ${kv('When', fmtDateTime(e.createdAt))}
        </div>
        <div class="detail-section-h">Identity & references</div>
        <div class="detail-grid">
          ${kv('Tx id', e.id)}
          ${kv('Request id', e.requestId)}
          ${kv('Ref tx id', e.refTxId)}
          ${kv('Parent tx id', e.parentTxId)}
          ${kv('Integrator tx id', e.integratorTxId)}
          ${kv('Player', e.playerUsername)}
          ${kv('Player id', e.playerId)}
        </div>
        <div class="detail-section-h">Reason</div>
        <pre class="detail-json">${e.reason || '—'}</pre>
        ${errBlock}
        <div style="margin-top:14px">
          <a href="${playerHref}" id="wl-detail-player" class="primary" style="text-decoration:none;padding:8px 12px;border-radius:8px;display:inline-block">Open player →</a>
        </div>
      `,
    });
    // The nav interceptor will route the link click, but won't dismiss the
    // modal — close it ourselves so it doesn't linger over the player page.
    document
      .querySelector<HTMLAnchorElement>('#modal-root #wl-detail-player')
      ?.addEventListener('click', () => close());
  }

  function bindReplay(): void {
    listEl.querySelectorAll<HTMLButtonElement>('.wl-replay').forEach((btn) => {
      btn.addEventListener('click', async (ev) => {
        ev.stopPropagation();
        if (!(await askConfirm('Replay this wallet call against the integrator?\n\nIdempotent — if the original was applied, the replay returns the existing tx unchanged.'))) return;
        const id = btn.dataset.id!;
        btn.disabled = true;
        const orig = btn.textContent;
        btn.textContent = '…';
        try {
          const r = await deps.client.replayWalletCall(id);
          const entry = accumulated.find((x) => x.id === id);
          const balance = entry
            ? `${fmtMoney(r.newBalanceCredits, cur(entry))} ${cur(entry)}`
            : r.newBalanceCredits;
          showToast(`Replay ${r.replayResult} — new balance ${balance}`, 'success');
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
          btn.disabled = false;
          btn.textContent = orig;
        }
      });
    });
  }

  function bindDrill(): void {
    listEl.querySelectorAll<HTMLTableRowElement>('tr.wl-row').forEach((row) => {
      row.addEventListener('click', (ev) => {
        // Skip when click came from an interactive child (button/link).
        const target = ev.target as HTMLElement;
        if (target.closest('button,a')) return;
        const entry = accumulated.find((e) => e.id === row.dataset.rowId);
        if (entry) openLedgerDetail(entry);
      });
    });
  }

  root.querySelector<HTMLButtonElement>('#wl-run')!.addEventListener('click', () => void refresh());
  root.querySelector<HTMLSelectElement>('#wl-since')!.addEventListener('change', (ev) => {
    const custom = (ev.target as HTMLSelectElement).value === 'custom';
    root.querySelector<HTMLElement>('#wl-custom-range')!.classList.toggle('hidden', !custom);
  });

  mountColumnPicker(root.querySelector<HTMLElement>('#wl-cols')!, {
    pageKey: 'wallet-ledger',
    columns: [
      'When',
      'Type',
      'Status',
      'Amount',
      'Balance after',
      'Fiat',
      'Currency',
      'Player',
      'Latency',
      'Integrator tx',
      'Reason',
      'Request ID',
      'Actions',
    ],
    onChange: () => applyHiddenCols(listEl.querySelector<HTMLElement>('table'), 'wallet-ledger'),
  });

  mountSavedViews(root.querySelector<HTMLElement>('#wl-views')!, 'wallet-ledger', {
    capture: () => ({
      playerId: root.querySelector<HTMLInputElement>('#wl-player')!.value,
      type: root.querySelector<HTMLSelectElement>('#wl-type')!.value,
      currency: root.querySelector<HTMLInputElement>('#wl-currency')!.value,
      since: root.querySelector<HTMLSelectElement>('#wl-since')!.value,
      from: root.querySelector<HTMLInputElement>('#wl-from')!.value,
      to: root.querySelector<HTMLInputElement>('#wl-to')!.value,
      limit: root.querySelector<HTMLInputElement>('#wl-limit')!.value,
    }),
    apply: (values) => {
      root.querySelector<HTMLInputElement>('#wl-player')!.value = values.playerId ?? '';
      root.querySelector<HTMLSelectElement>('#wl-type')!.value = values.type ?? '';
      root.querySelector<HTMLInputElement>('#wl-currency')!.value = values.currency ?? '';
      const sinceEl = root.querySelector<HTMLSelectElement>('#wl-since')!;
      sinceEl.value = values.since ?? '';
      root.querySelector<HTMLElement>('#wl-custom-range')!.classList.toggle('hidden', sinceEl.value !== 'custom');
      root.querySelector<HTMLInputElement>('#wl-from')!.value = values.from ?? '';
      root.querySelector<HTMLInputElement>('#wl-to')!.value = values.to ?? '';
      root.querySelector<HTMLInputElement>('#wl-limit')!.value = values.limit ?? '50';
      void refresh();
    },
  });

  root.querySelector<HTMLButtonElement>('#wl-export')!.addEventListener('click', () => {
    if (accumulated.length === 0) {
      showToast('No ledger entries to export.', 'warn');
      return;
    }
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    exportXlsx(
      `apx-wallet-ledger-${stamp}.xlsx`,
      accumulated.map((e) => ({
        id: e.id,
        createdAt: e.createdAt,
        playerId: e.playerId,
        playerUsername: e.playerUsername ?? '',
        type: e.type,
        // Amounts as decimals in the player's currency ("1234.56" in USD).
        currency: cur(e),
        creditAmount: toDecimalString(e.creditAmount, cur(e)),
        fiatAmount: e.fiatAmount == null ? '' : toDecimalString(e.fiatAmount, e.fiatCurrency || cur(e)),
        fiatCurrency: e.fiatCurrency,
        ratePpm: e.ratePpm ?? '',
        balanceAfter: e.balanceAfter == null ? '' : toDecimalString(e.balanceAfter, cur(e)),
        reason: e.reason,
        requestId: e.requestId,
        refTxId: e.refTxId ?? '',
      })),
    );
    showToast(`Exported ${accumulated.length} ledger entries.`, 'success');
  });

  await refresh();
}

/** The currency a row's amounts are counted in: the player's, which every
 *  wallet call is booked in. Falls back to the call's stamped currency for a
 *  response from an API build that predates the field. */
function cur(e: WalletLedgerEntry): string {
  return e.currency || e.fiatCurrency;
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
